import { ReactNode, useEffect } from "react";
import { Button, FormControl, FormControlLabel, InputLabel } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import { useTrans } from "~/hooks/useTrans";
import { IAdminSearchProjectFilter } from "~/providers/admin-interfaces";
import { myUseState } from "~/hooks/myUseState";
import TextFieldEnter from "~/mui/TextFieldEnter";
import { MyMenuItem, MySelect } from "~/mui/MySelect";
import { checkForErrorsState } from "~/utils/utils";
import { MySwitch } from "~/mui/MySwitch";
import BussinessPlanSelect from "../BussinessPlanSelect";
import BillingMethodSelect from "../BillingMethodSelect";
import ConsumedResourcesSelect from "../ConsumedResourcesSelect";

import "./style.less";

type TCanConsume = "none" | "active" | "inactive";
type TBoolean = "none" | "true" | "false";
type TApprovedAdvancedPlan = IAdminSearchProjectFilter["approvedAdvancedPlan"] | "none";

interface IStateError {
	keyword: string;
}

interface IAdminProjectsFilter {
	config: IAdminSearchProjectFilter;
	onSetFilters: (newFilter: IAdminSearchProjectFilter) => void;
	children: ReactNode,
}

export default function AdminProjectsFilter({
	config,
	onSetFilters,
	children,
}: IAdminProjectsFilter) {
	const { trans } = useTrans();
	const { state, setState, updateState } = myUseState<IAdminSearchProjectFilter>({
		keyword: "",
		...config,
		offset: 0,
	});
	const { state: stateError, setState: setStateError } = myUseState<IStateError>({
		keyword: "",
	});

	function setFilters() {
		if (state.keyword.length > 0) {
			const errorData = checkForErrorsState<IAdminSearchProjectFilter, IStateError>(state, {
				keyword: { check: "string-gt1", msg: trans("allProjectsPage.filter.keyword.error") },
			});

			setStateError(errorData.errorsState);

			if (errorData.errorsCount > 0) {
				return;
			}
		}

		const newFilter: IAdminSearchProjectFilter = {
			...state,
		};

		// smazeme booleany
		if ("vip0" in newFilter && !newFilter.vip0) {
			delete newFilter.vip0;
		}

		if ("actionRequired" in newFilter && !newFilter.actionRequired) {
			delete newFilter.vip0;
		}

		onSetFilters(newFilter);
	}

	function getProjectStateFilterValue(): TCanConsume {
		if ("canConsume" in state) {
			return state.canConsume
				? "active"
				: "inactive";
		}

		return "none";
	}

	function getProjectBilledConsumptionFilterValue(): TCanConsume {
		if ("billedConsumption" in state) {
			return state.billedConsumption
				? "active"
				: "inactive";
		}

		return "none";
	}

	function getProjectApprovedAdvancedPlanFilterValue(): TApprovedAdvancedPlan {
		if ("approvedAdvancedPlan" in state) {
			return state.approvedAdvancedPlan;
		}

		return "none";
	}

	function getProjectArchivedFilterValue(): TBoolean {
		if ("archived" in state) {
			return state.archived
				? "true"
				: "false";
		}

		return "none";
	}

	function updateFilterProjectState(newValue: TCanConsume) {
		const curFilter = {
			...state,
		};

		if (newValue === "none") {
			if ("canConsume" in curFilter) {
				delete curFilter.canConsume;
			}
		} else {
			curFilter.canConsume = newValue === "active";
		}

		setState(curFilter);
	}

	function updateFilterBilledConsumption(newValue: TCanConsume) {
		const curFilter = {
			...state,
		};

		if (newValue === "none") {
			if ("billedConsumption" in curFilter) {
				delete curFilter.billedConsumption;
			}
		} else {
			curFilter.billedConsumption = newValue === "active";
		}

		setState(curFilter);
	}

	function updateFilterApprovedAdvancedPlan(newValue: TApprovedAdvancedPlan) {
		const curFilter = {
			...state,
		};

		if (newValue === "none") {
			if ("approvedAdvancedPlan" in curFilter) {
				delete curFilter.approvedAdvancedPlan;
			}
		} else {
			curFilter.approvedAdvancedPlan = newValue;
		}

		setState(curFilter);
	}

	function updateFilterArchived(newValue: TBoolean) {
		const curFilter = {
			...state,
		};

		if (newValue === "none") {
			if ("archived" in curFilter) {
				delete curFilter.archived;
			}
		} else {
			curFilter.archived = newValue === "true";
		}

		setState(curFilter);
	}

	function updateFilterBusinessPlan(value: string | null) {
		const curFilter = {
			...state,
		};

		if (value === null) {
			if ("businessPlanType" in curFilter) {
				delete curFilter.businessPlanType;
			}
		} else {
			curFilter.businessPlanType = value;
		}

		setState(curFilter);
	}

	function updateBillingMethod(value: string | null) {
		const curFilter = {
			...state,
		};

		if (value === null) {
			if ("billingMethod" in curFilter) {
				delete curFilter.billingMethod;
			}
		} else {
			curFilter.billingMethod = value;
		}

		setState(curFilter);
	}

	function updateConsumedResources(value: string | null) {
		const curFilter = {
			...state,
		};

		if (value === null) {
			if ("consumedResources" in curFilter) {
				delete curFilter.consumedResources;
			}
		} else {
			curFilter.consumedResources = value;
		}

		setState(curFilter);
	}

	useEffect(() => {
		// reset filtru
		setState({
			keyword: "",
			...config,
			offset: 0,
		});
	}, [config]);

	return <div className="adminProjectsPage__filters">
		{ config && <div className="adminProjectsPage__filtersLine">
			<FormControl>
				<TextFieldEnter
					label={trans("allProjectsPage.filter.keyword")}
					value={state.keyword}
					onEnter={setFilters}
					onChange={event => updateState({ keyword: event.target.value.trim() })}
					color="success"
					size="small"
					sx={{ backgroundColor: "#fff" }}
					{ ...stateError.keyword
						? {
							error: true,
							helperText: stateError.keyword,
						}
						: {}
					}
				/>
			</FormControl>
			<FormControl size="small" sx={{ minWidth: "125px" }}>
				<InputLabel>
					{ trans("allProjectsPage.filter.projectState") }
				</InputLabel>
				<MySelect
					value={getProjectStateFilterValue()}
					onChange={event => updateFilterProjectState(event.target.value as TCanConsume)}
					label={trans("allProjectsPage.filter.projectState")}
				>
					<MyMenuItem value="none">
						{ trans("projectFilter.all") }
					</MyMenuItem>
					<MyMenuItem value="active">
						{ trans("allProjectsPage.filter.projectState.active") }
					</MyMenuItem>
					<MyMenuItem value="inactive">
						{ trans("allProjectsPage.filter.projectState.inactive") }
					</MyMenuItem>
				</MySelect>
			</FormControl>
			<BussinessPlanSelect value={state.businessPlanType} onChange={updateFilterBusinessPlan} />
			<BillingMethodSelect value={state.billingMethod} onChange={updateBillingMethod} />
			<FormControl size="small" sx={{ minWidth: "150px" }}>
				<InputLabel>
					{ trans("projectFinancialPage.paidConsumption.title") }
				</InputLabel>
				<MySelect
					value={getProjectBilledConsumptionFilterValue()}
					onChange={event => updateFilterBilledConsumption(event.target.value as TCanConsume)}
					label={trans("projectFinancialPage.paidConsumption.title")}
				>
					<MyMenuItem value="none">
						{ trans("projectFilter.all") }
					</MyMenuItem>
					<MyMenuItem value="active">
						{ trans("projectFinancialPage.paidConsumption.on") }
					</MyMenuItem>
					<MyMenuItem value="inactive">
						{ trans("projectFinancialPage.paidConsumption.off") }
					</MyMenuItem>
				</MySelect>
			</FormControl>
			<FormControl size="small" sx={{ minWidth: "150px" }}>
				<InputLabel>
					{ trans("allProjectsPage.filter.approvedAdvancedPlan.title") }
				</InputLabel>
				<MySelect
					value={getProjectApprovedAdvancedPlanFilterValue()}
					onChange={event => updateFilterApprovedAdvancedPlan(event.target.value as TApprovedAdvancedPlan)}
					label={trans("allProjectsPage.filter.approvedAdvancedPlan.title")}
				>
					<MyMenuItem value="none">
						{ trans("projectFilter.all") }
					</MyMenuItem>
					<MyMenuItem value="unset">
						{ trans("allProjectsPage.filter.projectState.none") }
					</MyMenuItem>
					<MyMenuItem value="approved">
						{ trans("allProjectsPage.filter.approvedAdvancedPlan.on") }
					</MyMenuItem>
					<MyMenuItem value="rejected">
						{ trans("allProjectsPage.filter.approvedAdvancedPlan.off") }
					</MyMenuItem>
				</MySelect>
			</FormControl>
			<FormControl size="small" sx={{ minWidth: "150px" }}>
				<InputLabel>
					{ trans("allProjectsPage.filter.archived.title") }
				</InputLabel>
				<MySelect
					value={getProjectArchivedFilterValue()}
					onChange={event => updateFilterArchived(event.target.value as TBoolean)}
					label={trans("allProjectsPage.filter.archived.title")}
				>
					<MyMenuItem value="none">
						{ trans("projectFilter.all") }
					</MyMenuItem>
					<MyMenuItem value="true">
						{ trans("allProjectsPage.filter.archived.on") }
					</MyMenuItem>
					<MyMenuItem value="false">
						{ trans("allProjectsPage.filter.archived.off") }
					</MyMenuItem>
				</MySelect>
			</FormControl>
			<ConsumedResourcesSelect value={state.consumedResources} onChange={updateConsumedResources} />
			<FormControlLabel
				className="adminProjectsFiler__mySwitch"
				control={<MySwitch sx={{ m: 1 }} checked={state.vip0 || false} onChange={event => updateState({ vip0: event.target.checked })} />}
				label={trans("allProjectsPage.filter.vip0")}
				sx={{ marginBottom: "var(--margin-large)" }}
			/>
			<FormControlLabel
				className="adminProjectsFiler__mySwitch"
				control={<MySwitch sx={{ m: 1 }} checked={state.actionRequired || false} onChange={event => updateState({ actionRequired: event.target.checked })} />}
				label={trans("projectFilter.actionRequired")}
				sx={{ marginBottom: "var(--margin-large)" }}
			/>
		</div> }
		<div className="adminProjectsPage__btnsLine">
			{ children }
			{ config && <FormControl size="small">
				<Button color="success" variant="contained" onClick={setFilters} className="btn-with-icon">
					<SearchIcon /> { trans("allProjectsPage.setFilters") }
				</Button>
			</FormControl> }
		</div>
	</div>;
}
